/**
 * 백오피스(/main · /admin · /auth) Monolith 토큰.
 *
 * 색은 홈페이지 2안(monolith.tokens.ts)의 `mono` 를 그대로 쓴다 — 베이스 모노크롬, 액센트 0,
 * 강조는 블랙 하나(DESIGN_SYSTEM §2). 여기 있는 것은 백오피스에만 필요한 밀도 값(높이·폭)과
 * 화면 안에서 반복되는 클래스 조각이다. 값이 갈리면 이 파일이 맞다.
 *
 * 접두사 `bo` — 홈페이지의 `mn` 과 클래스 이름이 섞이지 않게 한다(클래스 이름은 전역이다, §"클래스 이름은 전역이다").
 */
import { mono, monoFont } from '@components/systems/monolith/monolith.tokens'

export { mono }

/**
 * 관리자 모드 — 사이드바 맨 위 관리자 모드 칸 + 상단 탭 바가 **예전처럼 파란 띠**(MUI blue 700)로 이어진다.
 * 사이드바 나머지·콘텐츠는 main 과 같다. 띠 위의 글자·밑줄은 흰색, 사이드바 메뉴 활성 룰은 같은 파랑(`accent`).
 * (2026-09-16 사용자 결정 — 잉크 띠·슬레이트 쉘·아이스 블루 칩은 전부 폐기)
 */
export const boAdmin = {
	bar: '#1976D2',
	barLine: 'rgba(255, 255, 255, 0.2)',
	barHover: 'rgba(255, 255, 255, 0.12)',
	text: 'rgba(255, 255, 255, 0.72)',
	textOn: '#FFFFFF',
	accent: '#1976D2',
} as const

/**
 * 주문 상태 색 (2026-09-17 사용자 결정 — 오더 관리·주문 자세히에서 상태를 한눈에).
 * §13.2 "상태는 색이 아니라 칩 단계로" 의 예외다: 주문 흐름(신규 → 준비 → 배송 → 완료 · 환불 · 삭제)만
 * 색을 갖고, 나머지 칩은 여전히 흑백 세 단계다. 옅은 바탕 + 진한 글자, 테두리는 바탕보다 한 단계 진하게.
 */
export const boStatus = {
	/** 도매 · 입금 대기 — 슬레이트(회청). DISPATCH_PLAN §5 */
	awaitingPayment: { bg: '#EEF2F6', line: '#C9D3DF', text: '#3B4C63' },
	new: { bg: '#E8F1FD', line: '#B9D2F5', text: '#1D5FBF' },
	preparing: { bg: '#FFF4DC', line: '#F1D48F', text: '#9A6200' },
	inTransit: { bg: '#EEEAFD', line: '#CFC4F5', text: '#5B3FC4' },
	completed: { bg: '#E4F5EA', line: '#B4E0C4', text: '#1E7A44' },
	refunded: { bg: '#FDE8EA', line: '#F3BBC2', text: '#B42335' },
	deleted: { bg: '#EFF0F2', line: '#DDE0E4', text: '#6B7280' },
} as const

export type BoStatusKey = keyof typeof boStatus

/** 상태 칩 클래스 — `boChip st-new` 처럼 쓴다. 모르는 값이면 기본 흑백 칩 */
export const boStatusChipClass = (status: string): string =>
	status in boStatus ? `boChip st-${status}` : 'boChip'

export const boFont = {
	/** 본문 — Archivo 에 한글 폴백. 백오피스 카피는 한국어라 폴백이 실제로 쓰인다 */
	ui: `"Archivo", "Apple SD Gothic Neo", "Pretendard", "Noto Sans KR", ${monoFont.sans}`,
	/** 숫자·코드 열 */
	mono: '"SF Mono", Menlo, Consolas, monospace',
	/** 브랜드 워드마크 "Judahs" — public/fonts/Audiowide-Regular.ttf (App.css @font-face). 대소문자 그대로, 다른 서체로 쓰지 않는다 */
	brand: 'audiowideRegular, "Archivo", sans-serif',
} as const

/** 밀도 — 홈페이지보다 촘촘하다 */
export const boSize = {
	/** 상단 탭 바 높이 */
	navH: 48,
	/** 사이드바 폭(열림/닫힘) — redux config 의 drawerWidth·closedDrawerWidth 와 같은 값 */
	sideW: 240,
	sideWClosed: 64,
	/** 그리드 행·헤더 높이 */
	rowH: 40,
	/** 버튼·입력 높이 */
	ctlH: 34,
	/** 콘텐츠 좌우 여백 */
	gutter: 'clamp(20px, 2.4vw, 36px)',
} as const

/**
 * 폭 등급 — 창 폭으로 판정한다, 기기명이 아니다 (RESPONSIVE_PLAN §3, M3 window size class).
 * 폰 < 600 · 태블릿 600~1199 · 데스크톱 ≥ 1200. 판정 훅은 `useBoSizeClass()` 하나.
 */
export const boBreak = {
	phoneMax: 600,
	tabletMax: 1200,
} as const

export type BoSizeClass = 'phone' | 'tablet' | 'desktop'

/** Emotion css() 안에서 문자열 키로 쓴다 — `[boMedia.phone]: { ... }` */
export const boMedia = {
	phone: `@media (max-width: ${boBreak.phoneMax - 1}px)`,
	tablet: `@media (min-width: ${boBreak.phoneMax}px) and (max-width: ${
		boBreak.tabletMax - 1
	}px)`,
	notDesktop: `@media (max-width: ${boBreak.tabletMax - 1}px)`,
	/** 폰 가로 — 높이가 모자라 상단 바를 접는다 */
	shortHeight: '@media (max-height: 479px)',
} as const

/** 등급별 밀도. 데스크톱은 boSize 와 같고, 폰은 터치 목표 44·본문 15 (RESPONSIVE_PLAN §3) */
export const boDensity: Record<
	BoSizeClass,
	{ rowH: number; ctlH: number; fontBody: number; fontLabel: number; gutter: string }
> = {
	phone: { rowH: 56, ctlH: 44, fontBody: 15, fontLabel: 12, gutter: '16px' },
	tablet: { rowH: 48, ctlH: 40, fontBody: 14, fontLabel: 11, gutter: '20px' },
	desktop: { rowH: 40, ctlH: 34, fontBody: 13, fontLabel: 11, gutter: boSize.gutter },
}

/** 모바일 쉘 치수 */
export const boMobile = {
	/** 폰 상단 바 · 태블릿 상단 바 */
	topBarH: 48,
	/** 태블릿 네비 레일 = 접힌 사이드바 */
	railW: 64,
	/** 태블릿 목록 판 */
	paneListW: 380,
	/** 태블릿 사이드 시트 · 폰 이외의 편집 시트 */
	sideSheetW: 440,
	/** 임시 드로어(폰·태블릿 펼침) */
	drawerW: 'min(300px, 86vw)',
	fabSize: 56,
	/** 카드 */
	cardPad: 16,
	cardGap: 8,
	/** 길게 누르기 → 선택 모드 */
	longPressMs: 500,
} as const

/** z-index 스케일 — 고정 요소가 겹칠 때 여기 값만 쓴다 (RESPONSIVE_PLAN §11) */
export const boZ = {
	sticky: 1,
	fab: 5,
	bottomBar: 6,
	nav: 7,
	drawer: 1200,
	stack: 1250,
	sheet: 1260,
	dialog: 1300,
} as const

/** 200ms ease-out, color·background·transform 만 (DESIGN_SYSTEM §9) */
export const boMotion =
	'color 200ms ease-out, background-color 200ms ease-out, border-color 200ms ease-out'

/** 얇은 포커스 링 — ink 1px, 오프셋 2 */
export const boFocus = {
	outline: 'none',
	'&:focus-visible': {
		outline: `1px solid ${mono.ink}`,
		outlineOffset: 2,
	},
}

const control = {
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 8,
	height: boSize.ctlH,
	padding: '0 16px',
	fontFamily: boFont.ui,
	fontSize: 13,
	fontWeight: 500,
	lineHeight: 1,
	borderRadius: 2,
	cursor: 'pointer',
	whiteSpace: 'nowrap' as const,
	transition: boMotion,
	...boFocus,
	'&:disabled': {
		cursor: 'default',
		opacity: 0.35,
	},
}

/**
 * 화면 공통 조각. 페이지 css() 안에서 `...boShared` 로 스프레드한다.
 * 페이지 = boWrap → boHeader(kicker + title + actions) → boSection 반복.
 */
export const boShared = {
	fontFamily: boFont.ui,
	color: mono.ink,
	textAlign: 'left' as const,
	WebkitFontSmoothing: 'antialiased' as const,

	'h1, h2, h3, p': { margin: 0 },

	/** 페이지 프레임 — 좌우 여백 */
	'.boWrap': {
		width: '100%',
		height: '100%',
		padding: `0 ${boSize.gutter}`,
		boxSizing: 'border-box' as const,
	},

	/** 페이지 헤더 — 키커 위, 제목 아래, 우측 액션 */
	'.boHeader': {
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'space-between',
		gap: 24,
		padding: '28px 0 20px',
		borderBottom: `1px solid ${mono.line}`,
		flexWrap: 'wrap' as const,
	},
	'.boHeaderActions': {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		flexWrap: 'wrap' as const,
	},

	/** 브랜드 워드마크 — "Judahs" 는 이 클래스로만 쓴다(DESIGN_SYSTEM §7·§13). 크기는 쓰는 곳에서 fontSize 로 */
	'.boBrand': {
		fontFamily: boFont.brand,
		fontWeight: 400,
		textTransform: 'none' as const,
		letterSpacing: '0.02em',
		lineHeight: 1,
		color: mono.ink,
	},

	/** 대문자 키커 (섹션 라벨) */
	'.boKicker': {
		fontSize: 11,
		letterSpacing: '.24em',
		textTransform: 'uppercase' as const,
		color: mono.faint,
		fontWeight: 500,
	},
	/** 페이지 제목 — Thin 이 크게 */
	'.boTitle': {
		fontSize: 26,
		fontWeight: 300,
		lineHeight: 1.15,
		letterSpacing: '-0.01em',
		color: mono.ink,
		marginTop: 8,
	},
	/** 섹션 제목 */
	'.boSectionTitle': {
		fontSize: 17,
		fontWeight: 400,
		lineHeight: 1.3,
		color: mono.ink,
	},
	/** 리드·설명 */
	'.boLead': {
		fontSize: 13,
		fontWeight: 300,
		lineHeight: 1.65,
		color: mono.dim,
		maxWidth: 560,
	},
	/** 메타 (날짜·상태 라벨) */
	'.boMeta': {
		fontSize: 11,
		letterSpacing: '.16em',
		textTransform: 'uppercase' as const,
		fontWeight: 500,
		color: mono.faint,
	},

	/** 헤어라인 밴드 */
	'.boSection': {
		padding: '24px 0',
		borderBottom: `1px solid ${mono.line}`,
	},

	/** 카드 = surface + 헤어라인, radius 0, 그림자 없음 */
	'.boCard': {
		background: mono.surface,
		border: `1px solid ${mono.line}`,
		borderRadius: 0,
	},

	/** 블랙 버튼 — 화면당 하나 */
	'.boBtn': {
		...control,
		background: mono.ctaBg,
		color: '#FFFFFF',
		border: 0,
		'&:hover:not(:disabled)': { background: mono.ink },
	},
	/** 고스트 버튼 — 보조 동작 */
	'.boBtnGhost': {
		...control,
		background: 'transparent',
		color: mono.ink,
		border: `1px solid ${mono.ink}`,
		'&:hover:not(:disabled)': { background: mono.ink, color: '#FFFFFF' },
	},
	/** 텍스트 버튼 — 목록 안 동작 */
	'.boBtnText': {
		...control,
		padding: '0 8px',
		background: 'transparent',
		color: mono.dim,
		border: 0,
		'&:hover:not(:disabled)': { color: mono.ink },
	},
	/** 밑줄 링크 */
	'.boLink': {
		fontSize: 13,
		fontWeight: 500,
		color: mono.ink,
		background: 'transparent',
		border: 0,
		padding: 0,
		paddingBottom: 1,
		borderBottom: `1px solid ${mono.ink}`,
		cursor: 'pointer',
		fontFamily: boFont.ui,
	},

	/** 칩 — 상태·분류. 정지 상태는 흑백, 채움/외곽/희미 세 단계로만 구분한다 */
	'.boChip': {
		display: 'inline-flex',
		alignItems: 'center',
		height: 22,
		padding: '0 9px',
		fontSize: 11,
		letterSpacing: '.12em',
		textTransform: 'uppercase' as const,
		fontWeight: 500,
		lineHeight: 1,
		borderRadius: 2,
		border: `1px solid ${mono.lineSoft}`,
		color: mono.dim,
		background: 'transparent',
		whiteSpace: 'nowrap' as const,
		'&.solid': { background: mono.ink, borderColor: mono.ink, color: '#FFFFFF' },
		'&.outline': { borderColor: mono.ink, color: mono.ink },
		'&.faint': { borderColor: 'transparent', color: mono.faint },
		// 주문 상태 — boStatus 팔레트. 단계 클래스(solid·outline·faint)와 같이 쓰지 않는다
		...Object.fromEntries(
			(Object.keys(boStatus) as BoStatusKey[]).map(k => [
				`&.st-${k}`,
				{
					background: boStatus[k].bg,
					borderColor: boStatus[k].line,
					color: boStatus[k].text,
				},
			]),
		),
	},

	/** 폼 라벨 */
	'.boLabel': {
		fontSize: 11,
		letterSpacing: '.18em',
		textTransform: 'uppercase' as const,
		color: mono.faint,
		fontWeight: 500,
	},
	/** 밑줄 입력 */
	'.boInput': {
		width: '100%',
		height: 40,
		padding: 0,
		border: 0,
		borderBottom: `1px solid ${mono.lineInput}`,
		borderRadius: 0,
		background: 'transparent',
		fontFamily: boFont.ui,
		fontSize: 14,
		fontWeight: 300,
		color: mono.ink,
		outline: 'none',
		boxSizing: 'border-box' as const,
		'&:focus': { borderBottomColor: mono.ink },
		'&.error': { borderBottomColor: mono.error },
		'&::placeholder': { color: mono.faint },
	},
	'.boField': { display: 'flex', flexDirection: 'column' as const, gap: 10 },
	'.boError': { fontSize: 12, color: mono.error, fontWeight: 400 },

	/** 스펙 행 — 라벨(고정폭) + 값 */
	'.boSpecRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 160px) minmax(0, 1fr)',
		gap: 16,
		alignItems: 'center',
		padding: '12px 0',
		borderBottom: `1px solid ${mono.line}`,
	},
	'.boSpecLabel': {
		fontSize: 11,
		letterSpacing: '.14em',
		textTransform: 'uppercase' as const,
		color: mono.faint,
		fontWeight: 500,
	},
	'.boSpecValue': { fontSize: 14, fontWeight: 300, color: mono.ink },

	/** 인덱스 행 — 홈 Support 문법의 축소판. hover → surface */
	'.boRow': {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
		padding: '14px 0',
		borderBottom: `1px solid ${mono.line}`,
		transition: boMotion,
		'&:hover': { background: mono.surface },
		'&.active': { background: mono.surface },
	},

	/** 큰 수 — 대시보드 지표. Thin 이 크게, 라벨은 키커 */
	'.boStat': { display: 'flex', flexDirection: 'column' as const, gap: 6 },
	'.boStatValue': {
		fontSize: 34,
		fontWeight: 200,
		lineHeight: 1,
		letterSpacing: '-0.01em',
		fontVariantNumeric: 'tabular-nums',
		color: mono.ink,
	},

	/** 빈 상태 — 아이콘 없이 한 줄 */
	'.boEmpty': {
		padding: '40px 0',
		fontSize: 13,
		fontWeight: 300,
		color: mono.faint,
		textAlign: 'center' as const,
	},

	'@media (prefers-reduced-motion: reduce)': {
		'*': { transition: 'none !important' },
	},
}

/**
 * 폰에서 표를 카드로 (2026-09-29 사용자 요청 — 「모바일 화면에 테이블이 있으면 안 된다」).
 * 데스크톱·태블릿 DOM 은 그대로 두고 폰에서만 `display` 를 갈아 끼운다:
 * `thead` 는 숨고, `tr` 이 카드 한 장, `td` 는 「라벨 — 값」 한 줄이 된다.
 * 라벨은 `<td data-label="수량">` 로 준다. 라벨 없이 제목처럼 설 칸은 `className="cardTitle"`,
 * 값만 오른쪽에 설 칸(버튼 등)은 `className="cardEnd"`.
 *
 *   `[boMedia.phone]: { '.lineTable': boTableAsCards }`
 */
export const boTableAsCards = {
	display: 'block',
	width: '100%',
	maxWidth: '100%',
	overflow: 'visible',
	borderCollapse: 'collapse' as const,
	'& thead': { display: 'none' },
	'& tbody, & tfoot': { display: 'block', width: '100%', minWidth: 0 },

	'& tbody tr': {
		display: 'block',
		width: '100%',
		padding: '12px 14px',
		marginBottom: 8,
		background: mono.surface,
		border: `1px solid ${mono.line}`,
	},
	'& tbody td': {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: 12,
		width: '100%',
		minWidth: 0,
		padding: '4px 0',
		border: 0,
		textAlign: 'left' as const,
		fontSize: 14,
		whiteSpace: 'normal' as const,
		overflowWrap: 'anywhere' as const,
		'&::before': {
			content: 'attr(data-label)',
			flexShrink: 0,
			fontSize: 10,
			letterSpacing: '.12em',
			textTransform: 'uppercase' as const,
			fontWeight: 500,
			color: mono.faint,
		},
		'&:empty': { display: 'none' },
	},
	/** 값 쪽은 오른쪽에 붙고 숫자는 자릿수를 맞춘다 */
	'& tbody td.num': {
		textAlign: 'right' as const,
		fontVariantNumeric: 'tabular-nums' as const,
	},
	/** 번호 열은 카드에서 의미가 없다 */
	'& tbody td.colIdx, & tbody td.idx': { display: 'none' },
	/** 카드 제목 — 라벨 없이 한 줄 위에 */
	'& tbody td.cardTitle': {
		display: 'block',
		paddingBottom: 6,
		fontSize: 15,
		fontWeight: 400,
		color: mono.ink,
		'&::before': { display: 'none' },
	},
	'& tbody td.cardEnd': {
		justifyContent: 'flex-end',
		paddingTop: 8,
		'&::before': { display: 'none' },
	},
	/** 입력·셀렉트는 값 자리를 채운다 */
	'& tbody td input, & tbody td select': { flex: 1, minWidth: 0, maxWidth: '70%' },

	/** 합계 — 카드가 아니라 스펙 행 */
	'& tfoot': { paddingTop: 4 },
	'& tfoot tr': {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: 12,
		padding: '5px 2px',
	},
	'& tfoot td': {
		display: 'block',
		width: 'auto',
		padding: 0,
		border: 0,
		fontSize: 14,
	},
	'& tfoot .totLabel': { textAlign: 'left' as const, color: mono.dim },
	'& tfoot tr.totPaid': {
		marginTop: 4,
		paddingTop: 10,
		borderTop: `1px solid ${mono.ink}`,
		td: { fontSize: 15, fontWeight: 500, color: mono.ink },
	},
} as const
